import React, { useMemo, useState } from "react";
import { Box, Button, ButtonBase, Checkbox, Chip, Collapse, InputAdornment, TextField, Typography } from "@mui/material";
import { ExpandMore, Search } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import { AgentTool } from "../../services/aiAgentsService";

// Tipos na ordem em que aparecem; o tipo vem da funcionalidade que a ferramenta exige (RN-556)
const KIND_LABELS: Record<string, string> = {
  agenda: "Agenda",
  "livro-caixa": "Livro-caixa",
  prontuario: "Prontuário",
  pacientes: "Pacientes",
  "base-conhecimento": "Base de conhecimento",
  geral: "Geral",
};
const KIND_ORDER = Object.keys(KIND_LABELS);
const ALL = "todos";

const SENSITIVITY: Record<string, { label: string; bg: string; fg: string }> = {
  administrativo: { label: "Administrativo", bg: colors.backgroundSubtle, fg: colors.textSecondary },
  identificacao: { label: "Identificação", bg: colors.warningSoft, fg: colors.warningSoftText },
  clinico_sensivel: { label: "Clínico sensível", bg: colors.errorSoft, fg: "#842029" },
};
const AREA_LABELS: Record<string, string> = { agenda: "Agenda", "livro-caixa": "Livro-caixa" };

const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

interface AgentToolsPickerProps {
  /** Ferramentas que o perfil-alvo permite (o teto); só estas aparecem. */
  tools: AgentTool[];
  /** Chaves ligadas no agente — inclui as fora do teto, que são preservadas ao gravar. */
  enabled: string[];
  onChange: (next: string[]) => void;
  /** Tipo em que a lista abre (o da área que usa o agente); nulo = todos. */
  initialKind?: string | null;
}

const AgentToolsPicker: React.FC<AgentToolsPickerProps> = ({ tools, enabled, onChange, initialKind }) => {
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<string>(() =>
    initialKind && tools.some((t) => t.kind === initialKind) ? initialKind : ALL
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const on = useMemo(() => new Set(enabled), [enabled]);
  const isOn = (key: string) => on.has(key);

  const countByKind = useMemo(() => {
    const counts: Record<string, number> = {};
    tools.forEach((t) => {
      counts[t.kind] = (counts[t.kind] ?? 0) + 1;
    });
    return counts;
  }, [tools]);

  const kinds = KIND_ORDER.filter((k) => countByKind[k]).concat(
    Object.keys(countByKind).filter((k) => !KIND_LABELS[k])
  );

  const term = fold(search.trim());
  const visible = tools.filter(
    (t) =>
      (kind === ALL || t.kind === kind) &&
      (!term || fold(t.label).includes(term) || fold(t.description).includes(term))
  );
  const groups = kinds
    .map((k) => ({ kind: k, items: visible.filter((t) => t.kind === k) }))
    .filter((g) => g.items.length > 0);

  const totalOn = tools.filter((t) => isOn(t.key)).length;

  const setMany = (keys: string[], value: boolean) => {
    const next = new Set(enabled);
    keys.forEach((k) => (value ? next.add(k) : next.delete(k)));
    onChange(Array.from(next));
  };

  const toggleGroup = (k: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const kindChip = (value: string, label: string, count: number) => {
    const selected = kind === value;
    return (
      <Chip
        key={value}
        label={`${label} · ${count}`}
        onClick={() => setKind(value)}
        aria-pressed={selected}
        sx={{
          height: 30,
          fontWeight: 600,
          fontSize: "0.8rem",
          fontVariantNumeric: "tabular-nums",
          bgcolor: selected ? colors.primaryText : colors.backgroundSubtle,
          color: selected ? "#fff" : colors.textPrimary,
          "&:hover": { bgcolor: selected ? colors.primaryText : colors.primarySoft },
        }}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar ferramenta"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 240px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: colors.textSecondary }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Buscar ferramenta" }}
        />
        <Typography
          sx={{ fontSize: 14, color: colors.textSecondary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          <Box component="strong" sx={{ color: colors.textPrimary }}>{totalOn}</Box> de {tools.length} ligadas
        </Typography>
      </Box>

      {kinds.length > 1 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }} role="group" aria-label="Filtrar por tipo">
          {kindChip(ALL, "Todos", tools.length)}
          {kinds.map((k) => kindChip(k, KIND_LABELS[k] ?? k, countByKind[k]))}
        </Box>
      )}

      {groups.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 3, color: colors.textSecondary, border: "1px dashed #ced4da", borderRadius: "8px" }}>
          <Typography variant="body2">Nenhuma ferramenta com “{search.trim()}”.</Typography>
          <Button size="small" onClick={() => setSearch("")} sx={{ mt: 0.5, textTransform: "none", color: colors.primaryText }}>
            Limpar busca
          </Button>
        </Box>
      ) : (
        groups.map((g) => {
          const keys = g.items.map((t) => t.key);
          const groupOn = keys.filter(isOn).length;
          const allOn = groupOn === keys.length;
          const open = !collapsed.has(g.kind);
          const label = KIND_LABELS[g.kind] ?? g.kind;
          return (
            <Box key={g.kind} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", overflow: "hidden" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: colors.backgroundSubtle,
                  pr: 1,
                }}
              >
                <ButtonBase
                  onClick={() => toggleGroup(g.kind)}
                  aria-expanded={open}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    justifyContent: "flex-start",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    textAlign: "left",
                    "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: -2 },
                  }}
                >
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      color: colors.textSecondary,
                      transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  />
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>{label}</Typography>
                  <Typography
                    sx={{ fontSize: 13, color: colors.textSecondary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                  >
                    {groupOn} de {keys.length}
                  </Typography>
                </ButtonBase>
                <Button
                  size="small"
                  onClick={() => setMany(keys, !allOn)}
                  sx={{ textTransform: "none", fontWeight: 600, color: colors.primaryText, whiteSpace: "nowrap", minHeight: 36 }}
                >
                  {allOn ? "Desligar todas" : "Ligar todas"}
                </Button>
              </Box>

              <Collapse in={open} timeout={180}>
                {g.items.map((t) => {
                  const checked = isOn(t.key);
                  const sens = SENSITIVITY[t.sensitivity];
                  return (
                    <Box
                      key={t.key}
                      component="label"
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        px: 1.5,
                        py: 1.25,
                        borderTop: "1px solid #e9ecef",
                        cursor: "pointer",
                        "&:hover": { bgcolor: colors.primaryLight },
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => setMany([t.key], !checked)}
                        size="small"
                        sx={{ p: 0.5, mt: -0.25, color: colors.textSecondary, "&.Mui-checked": { color: colors.primaryText } }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontWeight: 600, color: checked ? colors.textPrimary : colors.textSecondary }}>
                            {t.label}
                          </Typography>
                          {sens && t.sensitivity !== "administrativo" && (
                            <Chip
                              label={sens.label}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: sens.bg, color: sens.fg }}
                            />
                          )}
                          {t.area && (
                            <Chip
                              label={`Só na área ${AREA_LABELS[t.area] ?? t.area}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.7rem", color: colors.primaryText, borderColor: colors.primarySoft }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary }}>
                          {t.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default AgentToolsPicker;
